import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Loader2,
  Mail,
  Phone,
  Upload,
} from "lucide-react";
import Button from "../../components/ui/Button";
import Pagination from "../../components/ui/Pagination";
import SortableHeader from "../../components/ui/SortableHeader";
import Modal from "../../components/ui/Modal";
import ClienteForm from "./ClienteForm";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import { AdminService } from "../../../application/services/AdminService";
import DashboardLayout from "../../layouts/DashboardLayout";

const ClientesPage = () => {
  const { isSuperAdmin, user, loading: authLoading } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(25);
  const [sortConfig, setSortConfig] = useState({
    key: "fecha_registro",
    direction: "DESC",
  });

  const [showModal, setShowModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Estados para Importación
  const [importing, setImporting] = useState(false);
  const [fileToImport, setFileToImport] = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const fileInputRef = React.useRef(null);

  const toast = useToast();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileToImport(file);
    setShowImportConfirm(true);
    // Clean input value right away so change event triggers again for same file if cancelled/retried
    // But we kept the file object in state
  };

  const confirmImport = async () => {
    if (!fileToImport) return;

    setImporting(true);
    try {
      const result = await AdminService.importarClientes(fileToImport);
      setImportResults(result);
      setShowImportConfirm(false); // Close confirm, Result modal will open because importResults is set
      toast.success("Proceso finalizado");
    } catch (error) {
      console.error(error);
      toast.error("Error al importar clientes.");
      setShowImportConfirm(false);
    } finally {
      setImporting(false);
      setFileToImport(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const closeImportResults = () => {
    setImportResults(null);
    fetchData();
  };

  const fetchData = useCallback(
    async (
      currentPage = 1,
      search = searchTerm,
      currentLimit = limit,
      currentSort = sortConfig,
    ) => {
      setLoading(true);
      try {
        const promises = [
          AdminService.getAllClientes(
            false,
            currentPage,
            currentLimit,
            search,
            currentSort.key,
            currentSort.direction,
            showInactive,
          ),
        ];

        if (isSuperAdmin()) {
          promises.push(AdminService.getEmpresas());
        }

        const [clientesData, empresasData] = await Promise.all(promises);

        setClientes(clientesData?.items || clientesData || []);
        setTotalPages(clientesData?.totalPages || 1);
        setTotalItems(clientesData?.total || 0);
        setPage(currentPage);

        if (isSuperAdmin()) {
          const listaEmpresas = Array.isArray(empresasData)
            ? empresasData
            : empresasData?.data || [];
          setEmpresas(listaEmpresas);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error al cargar datos");
      } finally {
        setLoading(false);
      }
    },
    [isSuperAdmin, showInactive, searchTerm, limit, sortConfig, toast],
  );

  useEffect(() => {
    if (!authLoading) {
      fetchData(1, searchTerm, limit, sortConfig);
    }
  }, [authLoading, user, fetchData, searchTerm, limit, sortConfig]);

  const handleOpenCreate = () => {
    setSelectedCliente(null);
    setShowModal(true);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    fetchData(1, term, limit, sortConfig);
  };

  const handleSort = (key) => {
    let direction = "ASC";
    if (sortConfig.key === key && sortConfig.direction === "ASC") {
      direction = "DESC";
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    fetchData(1, searchTerm, limit, newSort);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    fetchData(1, searchTerm, newLimit, sortConfig);
  };

  const handleOpenEdit = (cliente) => {
    setSelectedCliente(cliente);
    setShowModal(true);
  };

  const handleDelete = (cliente) => {
    setSelectedCliente(cliente);
    setShowDeleteConfirm(true);
  };

  const handleSuccess = () => {
    setShowModal(false);
    fetchData(page);
  };

  const confirmDelete = async () => {
    try {
      await AdminService.deleteCliente(selectedCliente.id_cliente);
      toast.success("Cliente eliminado exitosamente");
      fetchData(page);
    } catch (error) {
      console.error("Error deleting cliente:", error);
      toast.error("Error al eliminar cliente");
    } finally {
      setShowDeleteConfirm(false);
      setSelectedCliente(null);
    }
  };

  // La filtración ahora es 100% en el servidor para soportar grandes volúmenes de datos.

  return (
    <DashboardLayout title="Gestión de Clientes">
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Clientes
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Administra los clientes registrados en el sistema.
            </p>
          </div>
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current.click()}
              className="flex items-center gap-2"
              disabled={importing}
            >
              {importing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Importar Excel
            </Button>
            <Button
              variant={showInactive ? "primary" : "secondary"}
              onClick={() => setShowInactive(!showInactive)}
              className={`flex items-center gap-2 ${showInactive ? "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200" : ""}`}
            >
              {showInactive ? "Ocultar Inactivos" : "Ver Inactivos"}
            </Button>
            <Button
              onClick={handleOpenCreate}
              className="flex items-center gap-2 bg-blue-600 shadow-sm hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Nuevo Cliente
            </Button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6 flex items-center">
          <Search className="w-5 h-5 text-gray-400 mr-3" />
          <input
            type="text"
            placeholder="Buscar por cédula, nombre, email, teléfono, sector, empresa..."
            className="flex-1 outline-none text-gray-600 placeholder-gray-400"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <SortableHeader
                    label="Código"
                    field="codigo"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                    className="px-2 w-32"
                  />
                  {isSuperAdmin() && (
                    <th className="px-2 py-3 text-xs uppercase font-bold text-gray-500 tracking-wider w-32">
                      Empresa
                    </th>
                  )}
                  <SortableHeader
                    label="Nombres"
                    field="nombres"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                    className="px-2 min-w-[120px]"
                  />
                  <SortableHeader
                    label="Apellidos"
                    field="apellidos"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                    className="px-2 min-w-[120px]"
                  />
                  <SortableHeader
                    label="Email"
                    field="email"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                    className="px-2 min-w-[150px]"
                  />
                  <SortableHeader
                    label="Teléfono"
                    field="telefono"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                    className="px-2 w-28"
                  />
                  <SortableHeader
                    label="Sector"
                    field="sector"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                    className="px-2 min-w-[120px]"
                  />
                  <SortableHeader
                    label="F. Registro"
                    field="fecha_registro"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                    className="!px-1 w-[1%] whitespace-nowrap"
                  />
                  <SortableHeader
                    label="Estado"
                    field="activo"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                    className="!px-1 w-[1%] whitespace-nowrap text-center"
                  />
                  <th className="px-1 py-3 text-right text-xs uppercase font-bold text-gray-500 tracking-wider w-[1%] whitespace-nowrap">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={isSuperAdmin() ? 10 : 9}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      <div className="flex justify-center items-center">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        Cargando clientes...
                      </div>
                    </td>
                  </tr>
                ) : clientes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isSuperAdmin() ? 10 : 9}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No se encontraron clientes
                    </td>
                  </tr>
                ) : (
                  clientes.map((cliente) => (
                    <tr
                      key={cliente.id_cliente}
                      className="hover:bg-gray-50 transition-colors border-b border-gray-100"
                    >
                      <td className="px-2 py-1.5 text-sm font-mono font-medium text-gray-900 whitespace-nowrap pr-16">
                        {cliente.codigo_cliente}
                      </td>
                      {isSuperAdmin() && (
                        <td className="px-2 py-1.5 text-sm text-gray-600 truncate max-w-[120px]">
                          {cliente.Empresa?.nombre || "N/A"}
                        </td>
                      )}
                      <td className="px-2 py-1.5 text-sm text-gray-900 font-medium truncate max-w-[200px]">
                        {cliente.nombres}
                      </td>
                      <td className="px-2 py-1.5 text-sm text-gray-900 font-medium truncate max-w-[200px]">
                        {cliente.apellidos}
                      </td>
                      <td className="px-2 py-1.5 text-sm text-gray-600 truncate max-w-[200px]">
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-gray-400 shrink-0" />
                          <span className="truncate">
                            {cliente.email_cliente || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-sm text-gray-600 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400 shrink-0" />
                          <span>{cliente.telefono_cliente || "-"}</span>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-sm text-gray-600 truncate max-w-[250px]">
                        {cliente.sector || "-"}
                      </td>
                      <td className="px-1 py-1.5 text-sm text-gray-500 whitespace-nowrap w-[1%]">
                        {new Date(cliente.fecha_registro).toLocaleDateString()}
                      </td>
                      <td className="px-1 py-1.5 text-center w-[1%] whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            cliente.activo
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                              : "bg-red-50 text-red-700 border border-red-100"
                          }`}
                        >
                          {cliente.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-1 py-1.5 text-right w-[1%] whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          {cliente.activo ? (
                            <>
                              <button
                                onClick={() => handleOpenEdit(cliente)}
                                className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(cliente)}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                title="Deshabilitar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedCliente(cliente);
                                AdminService.updateCliente(cliente.id_cliente, {
                                  activo: true,
                                }).then(() => {
                                  toast.success("Cliente reactivado");
                                  fetchData(page);
                                });
                              }}
                              className="text-blue-600 hover:text-blue-800 transition-colors text-xs font-bold"
                            >
                              Reactivar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={limit}
            onPageChange={(p) => fetchData(p, searchTerm, limit, sortConfig)}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        </div>

        {showModal && (
          <ClienteForm
            onClose={() => setShowModal(false)}
            onSuccess={handleSuccess}
            initialData={selectedCliente}
            empresas={empresas}
            isSuperAdmin={isSuperAdmin()}
            currentUserEmpresaId={user?.id_empresa}
          />
        )}

        {/* Modal de Confirmación de Importación */}
        <Modal
          isOpen={showImportConfirm}
          onClose={() => {
            setShowImportConfirm(false);
            setFileToImport(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
          title="Confirmar Importación"
          size="sm"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowImportConfirm(false);
                  setFileToImport(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Cancelar
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={confirmImport}
                disabled={importing}
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {importing ? "Procesando..." : "Sí, Importar"}
              </Button>
            </>
          }
        >
          <div className="text-gray-600">
            <p>
              ¿Estás seguro de que deseas importar el archivo{" "}
              <strong>{fileToImport?.name}</strong>?
            </p>
            <p className="text-sm mt-2 text-blue-600">
              Se procesarán los clientes y se omitirán los que ya existan por
              cédula.
            </p>
          </div>
        </Modal>

        {/* Modal de Resultados de Importación */}
        <Modal
          isOpen={!!importResults}
          onClose={closeImportResults}
          title="Resultados de Importación"
          size="md"
          footer={
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={closeImportResults}
            >
              Cerrar y Actualizar
            </Button>
          }
        >
          {importResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                  <div className="text-2xl font-bold text-green-600">
                    {importResults.imported}
                  </div>
                  <div className="text-xs text-green-700 uppercase font-semibold">
                    Importados
                  </div>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                  <div className="text-2xl font-bold text-yellow-600">
                    {importResults.skips}
                  </div>
                  <div className="text-xs text-yellow-700 uppercase font-semibold">
                    Duplicados
                  </div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                  <div className="text-2xl font-bold text-red-600">
                    {importResults.errors.length}
                  </div>
                  <div className="text-xs text-red-700 uppercase font-semibold">
                    Errores
                  </div>
                </div>
              </div>

              {importResults.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-red-700 mb-2 text-sm">
                    Detalle de Errores:
                  </h4>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 max-h-40 overflow-y-auto text-xs font-mono text-gray-700">
                    {importResults.errors.map((err, idx) => (
                      <div
                        key={idx}
                        className="mb-1 border-b border-gray-100 pb-1 last:border-0"
                      >
                        {err}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Eliminar Cliente"
          size="sm"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmDelete}
              >
                Eliminar
              </Button>
            </>
          }
        >
          <div className="text-gray-600">
            <p>
              ¿Estás seguro de que deseas eliminar al cliente{" "}
              <strong>{selectedCliente?.nombre_cliente}</strong>?
            </p>
            <p className="text-sm mt-2 text-red-500">
              Esta acción no se puede deshacer.
            </p>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
};
export default ClientesPage;
